import type { PopoverProps, Theme } from '@mui/material';
import { Paper, Popover, styled } from '@mui/material';
import type { IActionButtonRounded } from 'Components/ActionButtonRounded';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import { Container, Item } from 'Components/Grid';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import Icon from '../Icon';
import { useAppSelector } from 'Store/index';
import ActionButton from 'Components/ActionButton';

const ListArea = styled(Paper, {
  shouldForwardProp: prop => prop !== 'width',
})(({ width }: { width: number; theme?: Theme }) => ({
  width: width + 'rem',
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderBottomLeftRadius: '0.4rem',
  borderBottomRightRadius: '0.4rem',
}));

const ListItem = styled('button', {
  shouldForwardProp: prop => prop !== 'height',
})(({ height, theme }: { height: number; theme?: Theme }) => ({
  width: '100%',
  height: height + 'rem',
  fontFamily: 'inherit',

  backgroundColor: theme?.palette.primary.main,
  border: 'none',
  cursor: 'pointer',

  '&:hover': {
    backgroundColor: theme?.palette.primary.main + 'D9',
  },
  '&:last-of-type': {
    borderBottomLeftRadius: '0.4rem',
    borderBottomRightRadius: '0.4rem',
  },
}));

const ListItemText = styled('div', {
  shouldForwardProp: prop => prop !== 'fontSize',
})(({ fontSize }: { fontSize: number }) => ({
  color: 'white',
  fontSize: fontSize + 'px',
  fontWeight: 600,
  textTransform: 'uppercase',
  userSelect: 'none',
  textAlign: 'center',
  width: '100%',
}));

const Divider = styled('div')({
  width: '70%',
  height: '0.1rem',
  backgroundColor: 'white',
  margin: '0 auto',
});

const ButtonComponent = ({
  rounded,
  props,
}: {
  rounded: boolean;
  props: React.ComponentProps<typeof ActionButtonRounded | typeof ActionButton>;
}): React.JSX.Element => (rounded ? <ActionButtonRounded {...props} /> : <ActionButton {...props} />);

const DropDownButton = ({
  text,
  width = 25,
  height = 5,
  fontSize = 24,
  iconSize = 2.5,
  rounded = true,
  mainButtonOtherProps,
  buttons,
}: IDropDownButton) => {
  const [buttonListOpen, setButtonListOpen] = React.useState<boolean>(false);
  const toggleList = () => setButtonListOpen(!buttonListOpen);

  const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);

  const popOverHeight = buttons.length * height * 10 + (buttons.length - 1);

  const [openDirection, setOpenDirection] = React.useState<'up' | 'down'>('down');

  const handleClick = (event: React.MouseEvent): void => {
    const buttonRects = event.currentTarget.getBoundingClientRect();
    if (buttonRects.bottom + popOverHeight >= window.innerHeight) {
      setOpenDirection('up');
    }
    setAnchorEl(event.currentTarget);
  };

  const open = Boolean(anchorEl);

  const isCreating = useAppSelector((s: IState) => s.documents.isCreating);

  const iconColor = mainButtonOtherProps?.alternate ? 'white' : undefined;

  return (
    <>
      <ButtonComponent
        props={{
          text: (
            <Container alignItems={'center'}>
              <Item xs={true} style={{ paddingLeft: iconSize + 'rem' }}>
                <FormattedMessage id={text} />
              </Item>
              <Item>
                {open ? (
                  <Icon icon="close" size={iconSize} color={iconColor} />
                ) : (
                  <Icon icon="menu" size={iconSize} color={iconColor} />
                )}
              </Item>
            </Container>
          ),
          onClick: handleClick,
          loading: !!isCreating,
          width,
          height,
          fontSize,
          ...mainButtonOtherProps,
        }}
        rounded={rounded}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setOpenDirection('down');
        }}
        anchorOrigin={{
          vertical: openDirection === 'down' ? 'bottom' : 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: openDirection === 'down' ? 'top' : 'bottom',
          horizontal: 'center',
        }}
        slots={{ paper: ListArea }}
        slotProps={{ paper: { width, square: true } } as PopoverProps['slotProps']}
      >
        <div style={{ width: '100%' }}>
          {buttons.map((but, i) => (
            <React.Fragment key={but.text}>
              <ListItem
                key={but.text}
                height={height}
                onClick={(e: React.MouseEvent<Element, MouseEvent>) => {
                  but.onClick(e);
                  toggleList();
                }}
              >
                <ListItemText fontSize={fontSize}>
                  <FormattedMessage id={but.text} />
                </ListItemText>
              </ListItem>

              {i < buttons.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </div>
      </Popover>
    </>
  );
};

interface IDropDownButton {
  /** Main button text, locale id */
  text: string;
  width?: number;
  height?: number;
  fontSize?: number;
  iconSize?: number;
  rounded?: boolean;
  /** Main button other props as speficied in ActionButtonRounded */
  mainButtonOtherProps?: SafeOmit<Partial<IActionButtonRounded>, 'asyncOnClick'>;
  /** Button list buttons */
  buttons: Array<{
    text: string;
    onClick: (e: React.MouseEvent) => void;
    disabled?: boolean;
    disabledTooltip?: React.JSX.Element;
  }>;
}

export default DropDownButton;
